// Setup › Hours and holidays on the SettingsPage pattern (BL-189): the working hours that make a call or a
// visit after hours (RULE_SECTIONS), the company's holidays by year (holidays.list), and the codes for
// time that isn't on a job (nonJobCodes.list), activities and leave. As Hours.tsx's TimeTab draws them in
// Blueprint, with nothing changed: a holiday's row opens it to rename it or say it's not a holiday, Add a
// holiday and Add a code open a FormSheet; changing holidays is the schedulers' (staff.hours.manage), the
// codes settings.edit; while the company mirrors its import source both read, with the line (BL-014).
// Who's off is Schedule › Time off's in Blueprint. Riverstone keeps TimeTab.
import { useEffect, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { Button, Checkbox, Link, Segmented, SelectField, TextField } from '@flowology/ui-web/fl';
import {
  FormSheet,
  SettingGroup,
  SettingRow,
  SettingsPage,
  SettingsSection,
} from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { BP_LEDE } from './ledes.ts';
import { settingsPage } from './parts.ts';
import { settingsRights } from './settings-model.ts';
import { codeOf, codeTerms, hoursRights, type Holiday, type NonJobCode } from './hours-model.ts';
import { SetupRuleSections } from './SetupRules.tsx';

const say = (e: unknown) => (e instanceof RpcError ? e.message : 'Could not save. Try again.');

const todayIn = (timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(),
  );
const dayText = (date: string) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${date}T00:00:00Z`));

/** A line said after a change ("Christmas Eve on Thu, Dec 24 saved."), and a section's other note. */
const noteOf = (said: string | null, other?: string | false): ReactNode =>
  said || other ? (
    <>
      {other || null}
      {other && said ? ' ' : null}
      {said ? <span role="status">{said}</span> : null}
    </>
  ) : null;

export function TimePage({ nav, timeZone }: { nav: NavProps; timeZone: string }) {
  const held = nav.permissions ?? [];
  const rights = hoursRights(held);
  const mirroring = useMirror();
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('time').label ?? 'Hours and holidays',
            lede: BP_LEDE.time,
          }}
        >
          <SetupRuleSections page="time" top rights={settingsRights(held)} />
          <Holidays
            canEdit={rights.manage && !mirroring}
            mirrorLine={mirroring && (rights.manage || rights.codes)}
            timeZone={timeZone}
          />
          <NonJobCodes canEdit={rights.codes && !mirroring} />
          <SetupRuleSections page="time" rights={settingsRights(held)} />
        </SettingsPage>
      </main>
    </div>
  );
}

type HolidayDraft = Holiday & { renaming?: boolean };

function Holidays({
  canEdit,
  mirrorLine,
  timeZone,
}: {
  canEdit: boolean;
  mirrorLine: boolean;
  timeZone: string;
}) {
  const [year, setYear] = useState(() => Number(todayIn(timeZone).slice(0, 4)));
  const [rows, setRows] = useState<Holiday[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [again, setAgain] = useState(0);
  const [draft, setDraft] = useState<HolidayDraft | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    setError(null);
    void client
      .call<{ holidays: Holiday[] }>('holidays.list', { year })
      .then((r) => setRows(r.holidays))
      .catch((e: unknown) => setError(say(e)));
  }, [year, again]);
  const done = (words: string) => {
    setDraft(null);
    setSaid(words);
    setAgain((n) => n + 1);
  };
  const note = noteOf(said, mirrorLine && MIRROR_LINE);
  return (
    <>
      <SettingsSection
        title="Holidays"
        line="Nobody is booked on a holiday: the board shows it, and booking anyone that day warns."
        action={
          <Segmented
            label="Year"
            selectionMode="none"
            items={[
              { id: 'before', label: 'The year before', icon: 'chevron-left' },
              { id: 'shown', label: String(year) },
              { id: 'after', label: 'The year after', icon: 'chevron-right' },
            ]}
            on="shown"
            onAction={(id) => {
              if (id === 'before') setYear(year - 1);
              if (id === 'after') setYear(year + 1);
            }}
          />
        }
        {...(note ? { note } : {})}
        status={rows ? (rows.length ? 'ready' : 'empty') : error ? 'failed' : 'loading'}
        emptyText={`None set for ${year}.`}
        {...(error ? { failedText: error } : {})}
        onRetry={() => setAgain((n) => n + 1)}
        {...(canEdit
          ? {
              foot: (
                <Button onPress={() => setDraft({ date: `${year}-01-01`, name: '', paid: true })}>
                  Add a holiday
                </Button>
              ),
            }
          : {})}
      >
        {rows?.map((h) => (
          <SettingRow
            key={h.date}
            name={`${dayText(h.date)} ${h.name}`}
            {...(h.paid ? {} : { tag: 'unpaid' })}
            control={
              canEdit ? (
                <Link label={`Rename ${h.name}`} onPress={() => setDraft({ ...h, renaming: true })}>
                  Edit
                </Link>
              ) : (
                ''
              )
            }
          />
        ))}
      </SettingsSection>
      {/* over the page: the section's body is its empty line while the year has no holidays */}
      {draft && <HolidaySheet key={draft.date} draft={draft} onClose={() => setDraft(null)} onDone={done} />}
    </>
  );
}

function HolidaySheet({
  draft: opened,
  onClose,
  onDone,
}: {
  draft: HolidayDraft;
  onClose: () => void;
  onDone: (said: string) => void;
}) {
  const [d, setD] = useState(opened);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const remove = () => {
    setRemoving(true);
    client
      .call('holidays.remove', { date: d.date })
      .then(() => onDone(`${opened.name} is no longer a holiday.`))
      .catch((e: unknown) => setRemoveError(say(e)))
      .finally(() => setRemoving(false));
  };
  return (
    <FormSheet
      isOpen
      title={opened.renaming ? `Edit ${opened.name}` : 'Add a holiday'}
      onClose={onClose}
      submitLabel="Save the holiday"
      isDirty={d.name !== opened.name || d.date !== opened.date || d.paid !== opened.paid}
      isSubmitDisabled={!d.name.trim() || !d.date || removing}
      {...(removeError ? { note: removeError } : {})}
      onSubmit={async () => {
        try {
          await client.call('holidays.set', { date: d.date, name: d.name.trim(), paid: d.paid });
        } catch (e) {
          throw new Error(say(e), { cause: e });
        }
        onDone(`${d.name.trim()} on ${dayText(d.date)} saved.`);
      }}
    >
      <TextField
        label="Day"
        type="date"
        isDisabled={!!opened.renaming}
        value={d.date}
        onChange={(date) => setD({ ...d, date })}
      />
      <TextField
        label="Name"
        maxLength={80}
        placeholder="Thanksgiving"
        value={d.name}
        onChange={(name) => setD({ ...d, name })}
      />
      <Checkbox isSelected={d.paid} onChange={(paid) => setD({ ...d, paid })}>
        Paid
      </Checkbox>
      {/* a holiday's row opens it, and it's removed from here */}
      {opened.renaming && (
        <Link variant="danger" isDisabled={removing} onPress={remove}>
          It’s not a holiday
        </Link>
      )}
    </FormSheet>
  );
}

type CodeDraft = NonJobCode & { isNew: boolean };
const NEW_CODE: CodeDraft = {
  code: '',
  name: '',
  category: 'leave',
  paid: true,
  countsTowardOvertime: false,
  active: true,
  isNew: true,
};
const KIND_LABEL = { activity: 'Activities', leave: 'Leave' } as const;

function NonJobCodes({ canEdit }: { canEdit: boolean }) {
  const [rows, setRows] = useState<NonJobCode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [again, setAgain] = useState(0);
  const [draft, setDraft] = useState<CodeDraft | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    setError(null);
    void client
      .call<{ codes: NonJobCode[] }>('nonJobCodes.list', { includeInactive: true })
      .then((r) => setRows(r.codes))
      .catch((e: unknown) => setError(say(e)));
  }, [again]);
  const note = noteOf(said);
  return (
    <>
      <SettingsSection
        title="Time that isn’t on a job"
        line="Activities a technician starts on the iPad (lunch, the shop), and leave the office books for them (vacation, sick). Leave never reaches the iPad’s list."
        {...(canEdit
          ? {
              action: (
                <Button variant="primary" onPress={() => setDraft(NEW_CODE)}>
                  Add a code
                </Button>
              ),
            }
          : {})}
        {...(note ? { note } : {})}
        status={rows ? (rows.length ? 'ready' : 'empty') : error ? 'failed' : 'loading'}
        emptyText="No codes yet."
        {...(error ? { failedText: error } : {})}
        onRetry={() => setAgain((n) => n + 1)}
      >
        {(['activity', 'leave'] as const).map((cat) => (
          <SettingGroup key={cat} label={KIND_LABEL[cat]} line={KIND_LABEL[cat]}>
            {(rows ?? [])
              .filter((c) => c.category === cat)
              .map((c) => (
                <SettingRow
                  key={c.code}
                  name={c.name}
                  help={codeTerms(c)}
                  control={
                    canEdit ? (
                      <Link label={`Edit ${c.name}`} onPress={() => setDraft({ ...c, isNew: false })}>
                        Edit
                      </Link>
                    ) : (
                      ''
                    )
                  }
                />
              ))}
          </SettingGroup>
        ))}
      </SettingsSection>
      {/* over the page: the section's body is its empty or failed line until it has codes */}
      {draft && (
        <CodeSheet
          key={draft.code || 'new'}
          draft={draft}
          onClose={() => setDraft(null)}
          onSaved={(name) => {
            setDraft(null);
            setSaid(`${name} saved.`);
            setAgain((n) => n + 1);
          }}
        />
      )}
    </>
  );
}

function CodeSheet({
  draft: opened,
  onClose,
  onSaved,
}: {
  draft: CodeDraft;
  onClose: () => void;
  onSaved: (name: string) => void;
}) {
  const [d, setD] = useState(opened);
  return (
    <FormSheet
      isOpen
      title={opened.isNew ? 'Add a code' : `Edit ${opened.name}`}
      onClose={onClose}
      submitLabel="Save the code"
      isDirty={JSON.stringify(d) !== JSON.stringify(opened)}
      isSubmitDisabled={!d.name.trim()}
      onSubmit={async () => {
        try {
          const r = await client.call<NonJobCode>('nonJobCodes.save', {
            code: d.isNew ? codeOf(d.name) : d.code,
            name: d.name.trim(),
            category: d.category,
            paid: d.paid,
            countsTowardOvertime: d.countsTowardOvertime,
            active: d.active,
          });
          onSaved(r.name);
        } catch (e) {
          throw new Error(say(e), { cause: e });
        }
      }}
    >
      <TextField
        label="Name"
        maxLength={60}
        placeholder="Bereavement"
        value={d.name}
        onChange={(name) => setD({ ...d, name })}
      />
      <SelectField
        label="Kind"
        value={d.category}
        options={[
          { value: 'leave', label: 'Leave the office books' },
          { value: 'activity', label: 'An activity the technician starts' },
        ]}
        onChange={(v) => {
          const category = v as NonJobCode['category'];
          setD({ ...d, category, countsTowardOvertime: category === 'activity' });
        }}
      />
      <Checkbox isSelected={d.paid} onChange={(paid) => setD({ ...d, paid })}>
        Paid
      </Checkbox>
      <Checkbox
        isSelected={d.countsTowardOvertime}
        onChange={(countsTowardOvertime) => setD({ ...d, countsTowardOvertime })}
      >
        Counts toward overtime
      </Checkbox>
      {!opened.isNew && (
        <Checkbox isSelected={d.active} onChange={(active) => setD({ ...d, active })}>
          In use (off: retired, kept on past time)
        </Checkbox>
      )}
    </FormSheet>
  );
}
